import { useState, useEffect } from "react";
import axios from "axios";
import PLAN_URL from "../../../utils/PLAN_URL";
import {
  Button,
  Card,
  CardContent,
  Typography,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
  Box,
  Tooltip,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { MaterialReactTable } from "material-react-table";
import styled from "styled-components";
import RemoveIcon from "@mui/icons-material/Remove";
import { useParams } from "react-router-dom";
import StockInModal from "./StockInModal";
import StockOutModal from "./StockOutModal";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100vh - 65px);
  background-color: var(--mui-palette-background-default);
  overflow: hidden;
  box-sizing: border-box;
`;

const FrameContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
  overflow: hidden;
  background-color: var(--mui-palette-background-paper);
  box-shadow: var(--mui-shadows-2);
`;

const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid var(--mui-palette-divider);
`;

const ToolbarLeft = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const ToolbarRight = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;

  /* 버튼 사이 간격 조정 */
  & > div {
    display: flex;
    gap: 0.5rem;
  }
`;

const StockSummaryContainer = styled.div`
  height: 25%;
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CardScrollContainer = styled.div`
  display: flex;
  overflow-x: auto;
  padding-bottom: 12px;
  margin-top: 12px;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #918fb5;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #918fb5;
  }
`;

const StockCard = styled(Card)`
  min-width: 200px;
  margin-right: 16px;
  flex-shrink: 0;
`;

const StockChangeIndicator = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
  font-size: 0.875rem;
  font-weight: 500;
`;

const TableContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  padding: 1rem;
`;

const TableControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px 16px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  gap: 8px;
`;

const PaginationButtonGroup = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  gap: 8px;
`;

const PaginationInfo = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const PaginationButton = styled(Button)`
  min-width: 40px;
  height: 40px;
  padding: 0;

  &.MuiButton-sizeSmall {
    min-width: unset;
    padding: 0 8px;
  }
`;

const InnerTableContainer = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* 테이블 스타일 오버라이드 */
  .MuiPaper-root {
    box-shadow: none;
    border-radius: 0;
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
  }

  .MuiTable-root {
    border-collapse: separate;
    border-spacing: 0;
  }

  .MuiTableContainer-root {
    flex: 1;
    overflow: auto;
  }

  .MuiTableHead-root {
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: var(--mui-palette-background-paper);
  }

  .MuiTableRow-root:hover {
    background-color: var(--mui-palette-action-hover);
  }
`;

function InOut() {
  const { group_code, project_code } = useParams();
  const [pagination, setPagination] = useState(30);
  const [inOutStatus, setInOutStatus] = useState({
    in: 1,
    out: 1,
  });
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [systemDate, setSystemDate] = useState("");
  //   const [totalStockData, setTotalStockData] = useState([]);
  const [inOutList, setInOutList] = useState([]);
  const [endPage, setEndPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [stockInModalOpen, setStockInModalOpen] = useState(false);
  const [stockOutModalOpen, setStockOutModalOpen] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(false);
  const handleOpenStockOutModal = () => {
    setStockInModalOpen(false);
    setStockOutModalOpen(true);
  };
  const handleOpenStockInModal = () => {
    setStockInModalOpen(true);
    setStockOutModalOpen(false);
  };
  const handleCloseStockInModal = () => {
    setStockInModalOpen(false);
    setStockOutModalOpen(false);
  };
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const res = await axios.get(
          PLAN_URL +
            `/api/stock/getSystem?group_code=${group_code}&project_code=${project_code}`
        );
        // const stockSummaryRes = await axios.get(
        //   PLAN_URL +
        //     `/api/stock/getSummary?group_code=${group_code}&project_code=${project_code}`
        // );
        setSystemDate(res.data);
        // setTotalStockData(stockSummaryRes.data);
      } catch (e) {
        console.log(e);
        openSnackbar("자료를 불러오지 못했습니다.", "error");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [group_code, project_code, updateStatus]);
  useEffect(() => {
    async function fetchData3() {
      try {
        const param = [
          group_code,
          project_code,
          inOutStatus.in,
          inOutStatus.out,
          pagination,
        ];
        const searchPageNumRes = await axios.post(
          PLAN_URL + "/api/stock/searchPageNum",
          param
        );
        setEndPage(searchPageNumRes.data.data);
      } catch (e) {
        console.log(e);
      }
    }
    fetchData3();
  }, [group_code, project_code, inOutStatus, pagination, updateStatus]);
  useEffect(() => {
    setLoading(true);
    async function fetchData2() {
      try {
        const param = [
          group_code,
          project_code,
          inOutStatus.in,
          inOutStatus.out,
          currentPageIndex,
          pagination,
        ];
        const inOutSearchResult = await axios.post(
          PLAN_URL + "/api/stock/searchInOut",
          param
        );
        setInOutList(inOutSearchResult.data);
      } catch (e) {
        console.log(e);
        openSnackbar("입출고 데이터를 불러오지 못했습니다.", "error");
      } finally {
        setLoading(false);
      }
    }
    fetchData2();
  }, [
    group_code,
    project_code,
    pagination,
    inOutStatus,
    currentPageIndex,
    updateStatus,
  ]);
  const handleToggleChange = (type) => {
    setInOutStatus((prev) => ({
      ...prev,
      [type]: prev[type] === 1 ? 0 : 1,
    }));
    setCurrentPageIndex(0);
  };
  const handlePaginationChange = (event) => {
    setPagination(Number(event.target.value));
    setCurrentPageIndex(0);
  };
  const handlePageChange = (pageIndex) => {
    setCurrentPageIndex(pageIndex);
  };
  const openSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };
  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  const formatQuantity = (quantity) => {
    return quantity.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") + " EA";
  };
  //   const renderChangeIndicator = (changeQty) => {
  //     const changeValue = Number(changeQty);
  //     let color, icon, formattedChange;
  //     if (changeValue > 0) {
  //       color = "#1976D2";
  //       icon = (
  //         <ArrowUpwardIcon fontSize="small" style={{ marginRight: "4px" }} />
  //       );
  //       formattedChange = `+${changeValue.toLocaleString()} EA`;
  //     } else if (changeValue < 0) {
  //       color = "#f44336";
  //       icon = (
  //         <ArrowDownwardIcon fontSize="small" style={{ marginRight: "4px" }} />
  //       );
  //       formattedChange = `${changeValue.toLocaleString()} EA`;
  //     } else {
  //       color = "#555555";
  //       icon = <RemoveIcon fontSize="small" style={{ marginRight: "4px" }} />;
  //       formattedChange = "0 EA";
  //     }
  //     return (
  //       <StockChangeIndicator style={{ color }}>
  //         {icon}
  //         {formattedChange}
  //       </StockChangeIndicator>
  //     );
  //   };
  const columns = [
    {
      accessorKey: "record_type",
      header: "구분",
      size: 100,
      enableSorting: false,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
      Cell: ({ row }) => (
        <Chip
          label={row.original.record_type === "in" ? "입고" : "출고"}
          size="small"
          sx={{
            backgroundColor:
              row.original.record_type === "in" ? "#1976d2" : "#ff8787",
            color: "white",
          }}
        />
      ),
    },
    {
      accessorKey: "id",
      header: "ID",
      size: 100,
      enableSorting: false,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
    },
    {
      accessorKey: "item_code",
      header: "제품 코드",
      size: 150,
      enableSorting: false,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
    },
    {
      accessorKey: "quantity",
      header: "수량",
      size: 120,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
      enableSorting: false,
      Cell: ({ cell }) => formatQuantity(cell.getValue()),
    },
    {
      accessorKey: "date",
      header: "날짜",
      size: 200,
      enableSorting: false,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
      Cell: ({ cell }) => new Date(cell.getValue()).toLocaleString(),
    },
    {
      accessorKey: "lot_code",
      header: "Lot 코드",
      size: 150,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
      enableSorting: false,
    },
    {
      accessorKey: "location_code",
      header: "위치 코드",
      size: 150,
      enableSorting: false,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
    },
    {
      accessorKey: "stock_in_id_ref",
      header: "입고 참조 ID",
      size: 150,
      enableSorting: false,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
    },
  ];
  const renderPaginationButtons = () => {
    const totalPages = Math.ceil(endPage / pagination);
    const buttons = [];
    buttons.push(
      <Tooltip title="처음으로 이동">
        <PaginationButton
          key="first"
          variant="outlined"
          disabled={currentPageIndex === 0}
          onClick={() => handlePageChange(0)}
          size="small"
          sx={{ minWidth: "unset" }}
        >
          &lt;&lt;
        </PaginationButton>
      </Tooltip>
    );
    buttons.push(
      <Tooltip title="이전 페이지">
        <PaginationButton
          key="prev"
          variant="outlined"
          size="small"
          sx={{ minWidth: "unset" }}
          disabled={currentPageIndex === 0}
          onClick={() => handlePageChange(Math.max(0, currentPageIndex - 1))}
        >
          &lt;
        </PaginationButton>
      </Tooltip>
    );
    const startPage = Math.max(
      0,
      Math.min(currentPageIndex - 2, totalPages - 5)
    );
    const endPageButton = Math.min(startPage + 5, totalPages);
    for (let i = startPage; i < endPageButton; i++) {
      buttons.push(
        <PaginationButton
          key={i}
          variant={i === currentPageIndex ? "contained" : "outlined"}
          onClick={() => handlePageChange(i)}
          size="small"
          sx={{ minWidth: "unset" }}
        >
          {i + 1}
        </PaginationButton>
      );
    }
    buttons.push(
      <Tooltip title="다음 페이지">
        <PaginationButton
          key="next"
          variant="outlined"
          disabled={currentPageIndex >= totalPages - 1}
          onClick={() =>
            handlePageChange(Math.min(totalPages - 1, currentPageIndex + 1))
          }
          size="small"
          sx={{ minWidth: "unset" }}
        >
          &gt;
        </PaginationButton>
      </Tooltip>
    );
    buttons.push(
      <Tooltip title="마지막으로 이동">
        <PaginationButton
          key="last"
          variant="outlined"
          disabled={currentPageIndex >= totalPages - 1}
          onClick={() => handlePageChange(totalPages - 1)}
          size="small"
          sx={{ minWidth: "unset" }}
        >
          &gt;&gt;
        </PaginationButton>
      </Tooltip>
    );
    return buttons;
  };

  return (
    <Wrapper>
      <FrameContainer>
        <ToolbarContainer>
          <ToolbarLeft>
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ fontSize: "22px !important", color: "#111" }}
              >
                입출고 현황
              </Typography>
            </div>
          </ToolbarLeft>
          <ToolbarRight>
            <div>
              <Typography variant="body2" sx={{ mr: 2 }}>
                시스템 날짜: {systemDate}
              </Typography>
            </div>
          </ToolbarRight>
        </ToolbarContainer>
        <TableContainer>
          <TableControlsContainer>
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={inOutStatus.in === 1}
                    onChange={() => handleToggleChange("in")}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#1976d2",
                        "&:hover": {
                          backgroundColor: "rgba(255, 135, 135, 0.08)",
                        },
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#1976d2",
                          opacity: 0.8,
                        },
                      "& .MuiSwitch-thumb": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                }
                label="입고 보기"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={inOutStatus.out === 1}
                    onChange={() => handleToggleChange("out")}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#ff8787",
                        "&:hover": {
                          backgroundColor: "rgba(255, 135, 135, 0.08)",
                        },
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#ff8787",
                          opacity: 0.8,
                        },
                      "& .MuiSwitch-thumb": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                }
                label="출고 보기"
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                sx={{
                  background: "#1976d2",
                  "&:hover": {
                    background: "#1565c0",
                  },
                }}
                onClick={handleOpenStockInModal}
              >
                입고 등록
              </Button>
              <Button
                variant="contained"
                sx={{
                  background: "#ff8787",
                  "&:hover": {
                    background: "#d17777",
                  },
                }}
                onClick={handleOpenStockOutModal}
              >
                출고 등록
              </Button>
              <Button
                variant="contained"
                sx={{
                  background: "#a1c789",
                  "&:hover": {
                    background: "#819e6f",
                  },
                }}
                onClick={() => {
                  openSnackbar("개발중입니다.", "info");
                }}
              >
                재고 정리
              </Button>
            </Stack>
          </TableControlsContainer>
          <InnerTableContainer>
            {loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress />
              </div>
            ) : (
              <MaterialReactTable
                columns={columns}
                data={inOutList || []}
                enableTopToolbar={false}
                enableBottomToolbar={false}
                enablePagination={false}
                enableSorting={false}
                enableColumnActions={false}
                enableGlobalFilter={false}
                muiTableProps={{
                  sx: {
                    tableLayout: "fixed",
                  },
                }}
                muiTableBodyProps={{
                  sx: {
                    "& tr:nth-of-type(odd)": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  },
                }}
                initialState={{
                  density: "compact",
                }}
                state={{
                  isLoading: loading,
                }}
              />
            )}
          </InnerTableContainer>
          <PaginationContainer>
            <div style={{ width: "200px" }}></div>{" "}
            <PaginationButtonGroup>
              {renderPaginationButtons()}
            </PaginationButtonGroup>
            <PaginationInfo>
              <FormControl
                variant="outlined"
                size="small"
                sx={{ minWidth: 120 }}
              >
                <InputLabel>페이지 크기</InputLabel>
                <Select
                  value={pagination}
                  onChange={handlePaginationChange}
                  label="페이지 크기"
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={30}>30</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
              <Paper elevation={0} sx={{ px: 2, py: 0.5 }}>
                <Typography variant="body2">
                  총 {endPage}개 항목 / {Math.ceil(endPage / pagination)} 페이지
                </Typography>
              </Paper>
            </PaginationInfo>
          </PaginationContainer>
        </TableContainer>
      </FrameContainer>
      <StockInModal
        open={stockInModalOpen}
        onClose={handleCloseStockInModal}
        openSnackbar={openSnackbar}
        setUpdateStatus={setUpdateStatus}
      />
      <StockOutModal
        open={stockOutModalOpen}
        onClose={handleCloseStockInModal}
        openSnackbar={openSnackbar}
        setUpdateStatus={setUpdateStatus}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Wrapper>
  );
}

export default InOut;
